import { placeKey } from "./customers.js";

/**
 * The week's plan, by arithmetic ([23](../../../docs/23-customers.md) §autopilot,
 * [24](../../../docs/24-everything-reach-does.md) §7, Overbill's ADR 0101 point 3).
 *
 * **The sharing out is sums, not a model.** The intelligence in this loop is in what is read at night, in one
 * bounded call; deciding where next week's letters go is division, and a model asked to do division would be a
 * model given the chance to be wrong about money. Everything in this file is pure, so it can be argued with.
 *
 * The unit is a **cell**: one group × one place × one source. It is the smallest thing a reply can honestly be
 * attributed to — *cafés, in France, found by asking AI* is a claim you can act on, where *cafés* alone is not.
 * A place is a country or one town inside it
 * ([ADR 0043](../../../docs/adr/0043-reach-is-a-list-of-places-and-a-place-is-a-country-or-one-town-inside-it.md)),
 * because *which of these four towns answers* is a question this arithmetic can answer and *does the UK work* is
 * not.
 * Only finders that search by words take part: a register searches by its own codes, so every group would bring
 * back the same companies from it and the cell would be measuring the register rather than the hypothesis.
 */

/** The sources a cell can be about. A register is not one of them, and that is the whole reason. */
export const CELL_SOURCES = ["ask_ai", "map"] as const;
export type CellSource = (typeof CELL_SOURCES)[number];

/** What one cell has actually done, counted from rows and never estimated. */
export interface CellRecord {
  groupId: string;
  country: string;
  /** The town inside it, or "" for the country taken whole (ADR 0043). */
  town: string;
  source: string;
  letters: number;
  /** Everybody who answered at all, whatever the answer turned out to be. */
  replies: number;
  /**
   * A call booked, and anything past it — **a subset of `replies`**, not a number beside it.
   *
   * Which is why the arithmetic below adds the two: a booked call is worth two answers, because an answer is
   * interest and a call is the thing the letters exist to produce.
   */
  meetings: number;
  bounced: number;
  optedOut: number;
}

/** A cell in next week's plan, and whether it is there to earn or to try. */
export interface PlannedCell {
  groupId: string;
  country: string;
  town: string;
  source: string;
  kind: "earn" | "try";
  letters: number;
  budgetMicro: bigint;
}

/**
 * How many letters a cell has to have before its own rate is believed.
 *
 * Under it, a cell's rate is pulled towards the average of every cell — so one reply out of two does not win the
 * week. This is the whole of the *lucky first letter* problem, and twenty is Overbill's number, arrived at by
 * running it.
 */
export const BELIEVE_AFTER = 20;

/** A cell's answers per letter, with a meeting worth two, pulled to the average while the cell is young. */
export function earns(cell: CellRecord, average: number): number {
  if (cell.letters <= 0) return 0;
  const raw = (cell.replies + cell.meetings) / cell.letters;
  // A weighted average of what this cell did and what everything did, the cell's own share rising with its letters.
  const trust = Math.min(1, cell.letters / BELIEVE_AFTER);
  return raw * trust + average * (1 - trust);
}

/** The average across every cell that has sent anything: one number, so a young cell has something to be pulled to. */
export function averageRate(cells: readonly CellRecord[]): number {
  const letters = cells.reduce((n, c) => n + c.letters, 0);
  if (letters <= 0) return 0;
  return cells.reduce((n, c) => n + c.replies + c.meetings, 0) / letters;
}

export interface PlanInput {
  /** Every cell that could run: those with a history, and those proposed or never tried. */
  cells: readonly CellRecord[];
  /** Cells to try even though they have no history: a new group, a kept group in another market. */
  trying: ReadonlyArray<CellKey>;
  /** The week's letters and the week's credits, both the owner's own numbers. */
  letters: number;
  budgetMicro: bigint;
  /** What share of the week goes to trying, in per cent. Never zero, or the loop stops learning. */
  exploreShare: number;
}

/**
 * Next week's letters, shared out.
 *
 * About 70% goes to the cells that earn most and about 30% to what has not been tried — the split is the
 * workspace's own `exploreShare`, and it never falls to zero, because a loop that only exploits is a loop that has
 * stopped learning and will never find the place it has not looked in.
 *
 * **Until letters have actually gone, every cell is trying.** The numbers mean nothing before then, so the whole
 * week is shared evenly rather than pretending a rate of zero out of zero is a ranking. That is not a special
 * case bolted on; it falls out of the arithmetic, and it is the state a new workspace is in for its first fortnight.
 */
export function sharePlan(input: PlanInput): PlannedCell[] {
  const letters = Math.max(0, Math.floor(input.letters));
  if (letters === 0) return [];

  const withHistory = input.cells.filter((c) => c.letters > 0);
  const average = averageRate(input.cells);
  const explore = Math.min(90, Math.max(5, input.exploreShare));

  // Nothing has been sent yet: there is nothing to rank, so everything is trying and the week is shared evenly.
  const tryCells = [...input.trying, ...input.cells.filter((c) => c.letters === 0)]
    .filter((c, i, all) => all.findIndex((o) => key(o) === key(c)) === i);
  if (withHistory.length === 0) {
    return evenly(tryCells, letters, input.budgetMicro, "try");
  }

  const forTrying = tryCells.length === 0 ? 0 : Math.round((letters * explore) / 100);
  const forEarning = letters - forTrying;

  const scored = withHistory
    .map((c) => ({ cell: c, score: earns(c, average) }))
    .sort((a, b) => b.score - a.score);
  const total = scored.reduce((n, s) => n + s.score, 0);

  // Every cell that earns anything gets a share of what it earned. When none of them has earned anything at all —
  // letters went and nobody answered — they share it evenly, because the honest ranking between them is no ranking.
  const earning: PlannedCell[] = total > 0
    ? shareBy(scored.map((s) => ({ cell: s.cell, weight: s.score })), forEarning, "earn")
    : evenly(scored.map((s) => s.cell), forEarning, 0n, "earn");

  const trying = evenly(tryCells, forTrying, 0n, "try");
  return withBudget([...earning, ...trying], input.budgetMicro, letters);
}

/**
 * The key that makes a cell one thing: group, place, source.
 *
 * A cell whose town is empty keys exactly as it did before towns existed, so nothing already counted moves.
 */
export const key = (c: CellKey) => `${c.groupId}|${placeKey(c)}|${c.source}`;

/** The three things that name a cell. */
export type CellKey = Pick<CellRecord, "groupId" | "country" | "town" | "source">;

/** Shared by weight, with the remainder going to the heaviest — so the total is exactly what was asked for. */
function shareBy(
  weighted: ReadonlyArray<{ cell: CellKey; weight: number }>,
  letters: number, kind: PlannedCell["kind"],
): PlannedCell[] {
  const total = weighted.reduce((n, w) => n + w.weight, 0);
  if (total <= 0 || letters <= 0) return [];
  const out = weighted.map((w) => ({
    groupId: w.cell.groupId, country: w.cell.country, town: w.cell.town, source: w.cell.source, kind,
    letters: Math.floor((letters * w.weight) / total), budgetMicro: 0n,
  }));
  let left = letters - out.reduce((n, o) => n + o.letters, 0);
  for (let i = 0; left > 0 && out.length > 0; i = (i + 1) % out.length, left -= 1) out[i]!.letters += 1;
  return out.filter((o) => o.letters > 0);
}

/** Shared evenly, which is what *we do not know yet* looks like as arithmetic. */
function evenly(
  cells: ReadonlyArray<CellKey>,
  letters: number, budgetMicro: bigint, kind: PlannedCell["kind"],
): PlannedCell[] {
  if (!cells.length || letters <= 0) return [];
  const each = Math.floor(letters / cells.length);
  let left = letters - each * cells.length;
  const out = cells.map((c) => ({
    groupId: c.groupId, country: c.country, town: c.town, source: c.source, kind,
    letters: each + (left-- > 0 ? 1 : 0), budgetMicro: 0n,
  }));
  return budgetMicro > 0n ? withBudget(out.filter((o) => o.letters > 0), budgetMicro, letters) : out.filter((o) => o.letters > 0);
}

/** The credits follow the letters, because a letter is what costs money. Every micro-dollar is given out exactly once. */
function withBudget(cells: PlannedCell[], budgetMicro: bigint, letters: number): PlannedCell[] {
  if (!cells.length || letters <= 0 || budgetMicro <= 0n) return cells;
  let left = budgetMicro;
  cells.forEach((c, i) => {
    const share = i === cells.length - 1 ? left : (budgetMicro * BigInt(c.letters)) / BigInt(letters);
    c.budgetMicro = share;
    left -= share;
  });
  return cells;
}

/* ------------------------------------------------------------------------------------- holding and dropping -- */

/**
 * The upper end of what a cell's rate could honestly be, at 90% confidence (Wilson).
 *
 * Used rather than the rate itself because the question being asked is *might this still be good?*, and a cell
 * with two letters and no answer has not failed — it has barely been asked. The upper bound is what stops the loop
 * retiring a hypothesis it never really tried.
 */
export function couldBe(replies: number, letters: number, z = 1.2816): number {
  if (letters <= 0) return 1;
  const p = replies / letters;
  const z2 = z * z;
  const denominator = 1 + z2 / letters;
  const centre = p + z2 / (2 * letters);
  const spread = z * Math.sqrt((p * (1 - p)) / letters + z2 / (4 * letters * letters));
  return Math.min(1, (centre + spread) / denominator);
}

/** A hypothesis holds in a place while it could still be within two thirds of the best one there. */
export const HOLDS_WITHIN = 2 / 3;

export function holds(cell: CellRecord, bestRateInPlace: number): boolean {
  if (bestRateInPlace <= 0) return true;
  return couldBe(cell.replies + cell.meetings, cell.letters) >= bestRateInPlace * HOLDS_WITHIN;
}

/**
 * Whether a cell has stopped itself, and in what words.
 *
 * Bounces and opt-outs past the workspace's own lines are not a performance problem, they are the thing that
 * closes a sending account, so a cell crossing one stops and waits for a person rather than slowing down. Below
 * ten letters nothing pauses: one bounce out of three is not a pattern, it is three letters.
 */
export const PAUSE_AFTER = 10;

export function shouldPause(cell: CellRecord, lines: { bounceTenths: number; optoutTenths: number }): string | null {
  if (cell.letters < PAUSE_AFTER) return null;
  const tenths = (n: number) => Math.round((n / cell.letters) * 1000);
  const bounced = tenths(cell.bounced), optedOut = tenths(cell.optedOut);
  if (bounced >= lines.bounceTenths) {
    return `${(bounced / 10).toFixed(1)}% of its letters bounced, past the ${(lines.bounceTenths / 10).toFixed(1)}% this workspace allows`;
  }
  if (optedOut >= lines.optoutTenths) {
    return `${(optedOut / 10).toFixed(1)}% asked to be left alone, past the ${(lines.optoutTenths / 10).toFixed(1)}% this workspace allows`;
  }
  return null;
}

/** The Monday of a date, as `YYYY-MM-DD`, so a week is one comparable key in every time zone this sells in. */
export function weekOf(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  // getUTCDay is 0 on Sunday, and a week here starts on Monday.
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

/* ---------------------------------------------------------------------------------- a group's own limits -- */

/**
 * The decisions Joogoos makes inside a group that the owner may take back with *Set it myself*
 * ([38](../../../docs/38-customers-by-group.md) step 3, ADR 0068 point 4). Each is a kind of proposal the night's
 * reading writes; taken back, a proposal of that kind for that group waits for the owner whatever the dial says.
 */
export const GROUP_DECISIONS = ["places", "words", "narrower", "versions"] as const;
export type GroupDecision = (typeof GROUP_DECISIONS)[number];

/** Which decision a proposal of each kind is. A retirement is none of them: it always waits for a person. */
export const DECISION_OF: Readonly<Record<string, GroupDecision | undefined>> = {
  try_market: "places", stop_place: "places", reword: "words", narrow: "narrower", call_test: "versions",
};

/**
 * **What Joogoos may do alone** (38 step 6, ADR 0068 point 5): four steps the owner moves one at a time.
 *
 * - `propose` — it writes and proposes; the owner says yes to everything.
 * - `run` — **run groups**: inside each group's limits it changes the words a group looks for, tries a place and
 *   stops one, and calls a test between two versions (`RUN_APPLIES`).
 * - `send` — on a live box it sends the letters it drafted, unedited, up to each group's number a day, inside every
 *   other limit (ADR 0069, which amends ADR 0039 point 8). A group's first ten letters sent this way each wait a
 *   morning on Today first (`goesAloneAt`).
 * - `grow` — it opens a new group from one that works, at half the limits of the one it grew from (ADR 0069 point 5).
 *
 * Kept in three columns of the loop: `learning_applies` (`none` is Propose, `words` Run groups), `sends_alone` and
 * `grows`. Each step needs the one before it, which the table checks.
 */
export const ALONE_STEPS = ["propose", "run", "send", "grow"] as const;
export type AloneStep = (typeof ALONE_STEPS)[number];

/** What Run groups lets through without a person. A retirement and a narrower group (a new group, so Grow) are never in it. Grow adds `narrow`. */
export const RUN_APPLIES = ["reword", "try_market", "stop_place", "call_test"] as const;

/** The step a workspace is on, read from its loop's columns. */
export const aloneStep = (loop: { learningApplies: string; sendsAlone?: boolean | undefined; grows?: boolean | undefined }): AloneStep =>
  loop.grows ? "grow" : loop.sendsAlone ? "send" : loop.learningApplies === "none" ? "propose" : "run";

/** The columns a step is, so the three can never disagree. */
export const stepColumns = (step: AloneStep): { learningApplies: "none" | "words"; sendsAlone: boolean; grows: boolean } => ({
  learningApplies: step === "propose" ? "none" : "words",
  sendsAlone: step === "send" || step === "grow",
  grows: step === "grow",
});

/** Whether moving from one step to another is allowed: back as far as you like, forward one step at a time. */
export const stepAllowed = (from: AloneStep, to: AloneStep): boolean =>
  ALONE_STEPS.indexOf(to) <= ALONE_STEPS.indexOf(from) + 1;

/** How many of a group's letters Joogoos sends on its own before they stop waiting a morning first (ADR 0069 point 4). */
export const WATCHED_LETTERS = 10;

/** The hour, UTC, of the morning a watched letter waits for. */
export const MORNING_HOUR_UTC = 8;

/**
 * When a letter the loop drafted may go on its own (ADR 0069 point 4). One of a group's first ten waits one morning on
 * Today, where *Stop it* takes it back: it goes after the first 08:00 UTC at least an hour after it was drafted, so it
 * is on the list for a morning whatever time of night it was written. After ten, it goes as it is drafted.
 */
export function goesAloneAt(draftedAt: Date, sentAloneInGroup: number): Date {
  if (sentAloneInGroup >= WATCHED_LETTERS) return draftedAt;
  const earliest = new Date(draftedAt.getTime() + 3_600_000);
  const morning = new Date(Date.UTC(earliest.getUTCFullYear(), earliest.getUTCMonth(), earliest.getUTCDate(), MORNING_HOUR_UTC));
  return morning.getTime() >= earliest.getTime() ? morning : new Date(morning.getTime() + 86_400_000);
}

/** A grown group's limits: half its parent's, rounded down, and *shared* where the parent's were (ADR 0069 point 5). */
export function halfLimits(parent: { lettersPerWeek: number | null; weeklyBudgetMicro: bigint | null; lettersPerDay: number }) {
  return {
    lettersPerWeek: parent.lettersPerWeek === null ? null : Math.floor(parent.lettersPerWeek / 2),
    weeklyBudgetMicro: parent.weeklyBudgetMicro === null ? null : parent.weeklyBudgetMicro / 2n,
    lettersPerDay: Math.floor(parent.lettersPerDay / 2),
  };
}

/** A group's own weekly limits, each null for *the workspace's, shared*. */
export interface GroupWeek {
  groupId: string;
  lettersPerWeek: number | null;
  weeklyBudgetMicro: bigint | null;
}

/**
 * The week's plan with each group held to its own limits.
 *
 * A group's cells keep their shares of one another; only their total comes down to the group's number, and what it
 * gives back is not handed to another group — the workspace's weekly total is a ceiling, not a quota to fill. So a
 * group's limit only ever narrows the plan, and every group together stays inside the workspace's week.
 */
export function withinGroupLimits(planned: readonly PlannedCell[], limits: readonly GroupWeek[]): PlannedCell[] {
  const out = planned.map((c) => ({ ...c }));
  for (const limit of limits) {
    const cells = out.filter((c) => c.groupId === limit.groupId);
    if (!cells.length) continue;
    const letters = cells.reduce((n, c) => n + c.letters, 0);
    if (limit.lettersPerWeek !== null && letters > limit.lettersPerWeek) {
      const want = Math.max(0, limit.lettersPerWeek);
      const shared = cells.map((c) => Math.floor((c.letters * want) / letters));
      let left = want - shared.reduce((n, x) => n + x, 0);
      // The remainder to the biggest shares first, so the total is exactly the group's number.
      const order = cells.map((c, i) => i).sort((a, b) => cells[b]!.letters - cells[a]!.letters);
      for (let k = 0; left > 0; k = (k + 1) % order.length, left -= 1) shared[order[k]!]! += 1;
      cells.forEach((c, i) => {
        const was = c.letters;
        c.letters = shared[i]!;
        c.budgetMicro = was > 0 ? (c.budgetMicro * BigInt(c.letters)) / BigInt(was) : 0n;
      });
    }
    const budget = cells.reduce((n, c) => n + c.budgetMicro, 0n);
    if (limit.weeklyBudgetMicro !== null && budget > limit.weeklyBudgetMicro) {
      const want = limit.weeklyBudgetMicro < 0n ? 0n : limit.weeklyBudgetMicro;
      for (const c of cells) c.budgetMicro = budget > 0n ? (c.budgetMicro * want) / budget : 0n;
    }
  }
  return out.filter((c) => c.letters > 0);
}

/* ----------------------------------------------------------------------------- the plan in words (38 step 4) -- */

/** What Joogoos is doing in one group, in four short lines a person reads before any number. */
export interface PlanInWords {
  doing: string;
  next: string;
  then: string;
  learned: string;
}

/** What one group's lines are written from: its counts, and nothing a model said except `modelSaid`. */
export interface GroupWeekFacts {
  /** The loop's own switch, the group's pause, and whether it is retired. */
  running: boolean;
  paused: boolean;
  retired: boolean;
  /** This week's plan rows for the group. */
  week: ReadonlyArray<{ place: string; source: string; letters: number; drafted: number; found: number; searches: number; spentMicro: bigint; budgetMicro: bigint }>;
  /** Every letter it has sent, and what came back, across its cells. */
  letters: number;
  replies: number;
  /** Places of it that stopped themselves, and the proposals about it waiting for the owner. */
  stopped: number;
  waiting: number;
  /** The one sentence the night's reading wrote about this group, when it wrote one. */
  modelSaid: string | null;
  /** How a place, a source and an amount are said. */
  placeName: (place: string) => string;
  sourceName: (source: string) => string;
  usd: (micro: bigint) => string;
}

/**
 * The plan in words: **code counts**, and these lines are only the counts said plainly. The reading may phrase the
 * last one (`modelSaid`), through `runCall` and charged to the business, on a week somebody answered; on every
 * other week the counts say it themselves, because paying a model to repeat a number is the cost ADR 0039 point 5
 * exists to avoid.
 */
export function planInWords(f: GroupWeekFacts): PlanInWords {
  const learned = f.modelSaid?.trim()
    || (f.letters < BELIEVE_AFTER
      ? f.letters === 0
        ? "Nothing yet: no letter has gone from this group."
        : `Too early to tell: ${f.letters} ${f.letters === 1 ? "letter has" : "letters have"} gone, and a place is judged after ${BELIEVE_AFTER}.`
      : `${f.replies} of ${f.letters} letters answered.`);
  if (f.retired) return { doing: "Nothing: this group is retired.", next: "Its companies stay, under its name.", then: "Nothing else is planned for it.", learned };
  if (f.paused) return { doing: "Nothing: you paused this group.", next: "It starts again when you say so.", then: "Its week waits until then.", learned };
  if (!f.running) return { doing: "Nothing on its own: the loop is switched off in Settings.", next: "You can still find and write by hand.", then: "Nothing is planned until the loop runs.", learned };
  if (!f.week.length) return { doing: "Nothing planned this week yet.", next: "The week is planned on the loop's next run.", then: "Then it looks and writes inside your limits.", learned };

  const sum = (k: "letters" | "drafted" | "found" | "searches") => f.week.reduce((n, c) => n + c[k], 0);
  const spent = f.week.reduce((n, c) => n + c.spentMicro, 0n);
  const budget = f.week.reduce((n, c) => n + c.budgetMicro, 0n);
  const searches = sum("searches");
  const doing = "This week: " + `${sum("drafted")} of ${sum("letters")} letters written, ${sum("found")} companies found in ${searches} ${searches === 1 ? "search" : "searches"}, ` +
    "$" + f.usd(spent) + " of $" + f.usd(budget) + " spent.";
  const left = f.week.filter((c) => c.drafted < c.letters && c.spentMicro < c.budgetMicro)
    .sort((a, b) => (b.letters - b.drafted) - (a.letters - a.drafted));
  const next = left[0]
    ? `Next it looks in ${f.placeName(left[0].place)} through ${f.sourceName(left[0].source)}.`
    : "This group has had its week.";
  const then = f.waiting
    ? `Then it waits for your answer on ${f.waiting} ${f.waiting === 1 ? "proposal" : "proposals"}.`
    : f.stopped
      ? `Then it waits for you: ${f.stopped} ${f.stopped === 1 ? "place stopped itself" : "places stopped themselves"}.`
      : "Then next week is planned on Monday from what came back.";
  return { doing, next, then, learned };
}

/**
 * The places of a group worth stopping, from the counts alone (38 step 4: *a country to stop*).
 *
 * A place has had a fair run (`BELIEVE_AFTER` letters) and could not honestly be within `HOLDS_WITHIN` of where the
 * same group answers best. Compared within the group rather than across groups, because the question is *this offer,
 * here or there*; a group with one place has nothing to compare and is never told to stop.
 */
export function placesToStop(cells: readonly CellRecord[]): Array<{ groupId: string; place: string; letters: number; replies: number; bestPlace: string }> {
  const byGroupPlace = new Map<string, { groupId: string; place: string; letters: number; replies: number }>();
  for (const c of cells) {
    const k = `${c.groupId}|${placeKey(c)}`;
    const now = byGroupPlace.get(k) ?? { groupId: c.groupId, place: placeKey(c), letters: 0, replies: 0 };
    now.letters += c.letters;
    now.replies += c.replies + c.meetings;
    byGroupPlace.set(k, now);
  }
  const all = [...byGroupPlace.values()];
  const out: ReturnType<typeof placesToStop> = [];
  for (const groupId of new Set(all.map((p) => p.groupId))) {
    const places = all.filter((p) => p.groupId === groupId && p.letters >= BELIEVE_AFTER);
    const best = [...places].sort((a, b) => b.replies / b.letters - a.replies / a.letters)[0];
    if (!best || best.replies === 0) continue;
    const bestRate = best.replies / best.letters;
    for (const p of places) {
      if (p === best) continue;
      if (couldBe(p.replies, p.letters) < bestRate * HOLDS_WITHIN) {
        out.push({ groupId, place: p.place, letters: p.letters, replies: p.replies, bestPlace: best.place });
      }
    }
  }
  return out;
}

/**
 * What a yes to a proposal costs a week, at most (38 step 4). A place added is the share the week's plan gives a
 * place being tried, once for each source it is searched through; a narrower group is that for every place it is
 * looked for in; a change of words, a retirement and a stop cost nothing, and a stop saves. Never more than the
 * group's own money, when it has some. Inside the workspace's weekly total, never on top of it.
 */
export function priceOfYes(kind: string, ctx: { perTryCellMicro: bigint; places: number; groupBudgetMicro: bigint | null }): bigint {
  const cells = kind === "try_market" ? CELL_SOURCES.length : kind === "narrow" ? CELL_SOURCES.length * Math.max(1, ctx.places) : 0;
  const price = ctx.perTryCellMicro * BigInt(cells);
  return ctx.groupBudgetMicro !== null && price > ctx.groupBudgetMicro ? ctx.groupBudgetMicro : price;
}
